/**
 * Section Engine
 *
 * Given:
 *   - DCEL state (walls, openings, room heights)
 *   - A section cut line: { x1, y1, x2, y2 } in world mm
 *   - Looking direction: "left" | "right" (which side of the cut you're looking at)
 *
 * Produces:
 *   - sectionWalls[]   — walls intersected by the cut, with their profile geometry
 *   - visibleWalls[]   — walls visible beyond the cut (dashed)
 *   - groundLine       — finished floor level
 *   - ceilingLine      — ceiling/slab level
 *   - floorSlab        — floor slab thickness geometry
 *   - roofSlab         — roof slab (if drawRoof enabled)
 *   - annotations[]    — height/level annotations
 *
 * All output coordinates are in a local section space:
 *   x = distance along the cut line (mm)
 *   y = height above floor level (mm, positive upward)
 */

import { Geo } from "../utils/geo";
import { DEFAULT_H } from "../constants";

const FLOOR_SLAB_T  = 150;  // floor slab thickness mm
const ROOF_SLAB_T   = 150;  // roof/ceiling slab thickness mm
const PLINTH_H      = 450;  // plinth height above natural ground mm
const FOUND_DEPTH   = 600;  // foundation depth below plinth mm

/**
 * Projects a world point onto the cut line.
 * Returns { t, d } where t is parametric position along cut, d is signed distance.
 */
function projectOnCut(pt, cut) {
  const dx = cut.x2 - cut.x1, dy = cut.y2 - cut.y1;
  const len = Math.sqrt(dx*dx + dy*dy);
  if (len < 1) return null;
  const ux = dx/len, uy = dy/len;
  const rx = pt.x - cut.x1, ry = pt.y - cut.y1;
  const t = rx*ux + ry*uy;          // distance along cut
  const d = rx*(-uy) + ry*ux;        // signed perpendicular distance
  return { t, d, pos: t };
}

/**
 * Intersects a wall segment with the cut line.
 * Returns { t_cut, t_wall, point } or null.
 */
function wallCutIntersect(nA, nB, cut) {
  return Geo.sx(
    { x: cut.x1, y: cut.y1 },
    { x: cut.x2, y: cut.y2 },
    nA, nB
  );
}

/**
 * Main section derivation function.
 */
export function deriveSection(st, cut, lookDir = "left") {
  const cutDx = cut.x2 - cut.x1, cutDy = cut.y2 - cut.y1;
  const cutLen = Math.sqrt(cutDx*cutDx + cutDy*cutDy);
  if (cutLen < 100) return null;

  const sectionWalls  = [];  // walls cut by the section plane
  const visibleWalls  = [];  // walls visible beyond the cut
  const annotations   = [];

  // Normal to cut line — determines "in front" side
  const nx = -cutDy / cutLen, ny = cutDx / cutLen;
  // lookDir: "left" = positive normal side, "right" = negative normal side
  const lookSign = lookDir === "left" ? 1 : -1;

  for (const edge of Object.values(st.edges)) {
    const ab = st.halfEdges[edge.halfEdgeA], ba = st.halfEdges[edge.halfEdgeB];
    if (!ab || !ba) continue;
    const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
    if (!nA || !nB) continue;

    const wallH    = edge.height    || DEFAULT_H;
    const wallT    = edge.thickness || 230;
    const matId    = edge.materialId || "BRICK";

    // Does this wall intersect the cut plane?
    const ix = wallCutIntersect(nA, nB, cut);
    if (ix && ix.t >= -0.001 && ix.t <= 1.001 && ix.u >= -0.001 && ix.u <= 1.001) {
      // Cut position along section x-axis
      const sectionX = Math.round(ix.t * cutLen);

      // Wall direction in section: perpendicular thickness becomes width in section
      const wallDx = nB.x - nA.x, wallDy = nB.y - nA.y;
      const wallLen = Math.sqrt(wallDx*wallDx + wallDy*wallDy);

      // Half-thickness in the cut direction
      const tHalf = wallT / 2;

      // Openings on this wall
      const ops = Object.values(st.openings || {}).filter(o => o.edgeId === edge.id);
      const cutT = ix.u; // parametric position of cut on wall

      // Find openings that the cut passes through
      const cutOps = ops.filter(op => {
        const opStart = op.t - (op.width / 2 / wallLen);
        const opEnd   = op.t + (op.width / 2 / wallLen);
        return cutT >= opStart - 0.01 && cutT <= opEnd + 0.01;
      });

      sectionWalls.push({
        id:       edge.id,
        sectionX,
        wallH,
        wallT,
        matId,
        left:     sectionX - tHalf,
        right:    sectionX + tHalf,
        base:     0,
        top:      wallH,
        openings: cutOps.map(op => ({
          base: op.sillHeight || 0,
          top:  (op.sillHeight || 0) + op.height,
          type: op.sillHeight > 0 ? "window" : "door",
        })),
      });
    } else {
      // Wall is not cut — is it visible from the look direction?
      const midX = (nA.x + nB.x) / 2, midY = (nA.y + nB.y) / 2;
      const proj  = projectOnCut({ x: midX, y: midY }, cut);
      if (!proj) continue;

      // Signed distance from cut plane
      const dist = (midX - cut.x1) * nx + (midY - cut.y1) * ny;
      const isVisible = dist * lookSign > 0 && dist * lookSign < 8000; // within 8m

      if (isVisible) {
        // Project both endpoints onto section x-axis
        const pA = projectOnCut(nA, cut);
        const pB = projectOnCut(nB, cut);
        if (!pA || !pB) continue;

        const xA = Math.round(pA.t), xB = Math.round(pB.t);
        if (Math.abs(xB - xA) < 50) continue; // too short to draw

        visibleWalls.push({
          id:     edge.id,
          xA:     Math.min(xA, xB),
          xB:     Math.max(xA, xB),
          y:      0,
          height: edge.height || DEFAULT_H,
          matId,
          dist:   Math.abs(dist),
        });
      }
    }
  }

  // Sort cut walls by x position
  sectionWalls.sort((a, b) => a.sectionX - b.sectionX);

  // Section extent
  const allX = sectionWalls.flatMap(w => [w.left, w.right])
    .concat(visibleWalls.flatMap(w => [w.xA, w.xB]));
  const minX = allX.length ? Math.min(...allX) - 500 : -500;
  const maxX = allX.length ? Math.max(...allX) + 500 : cutLen + 500;
  const maxH  = Math.max(...sectionWalls.map(w => w.wallH), DEFAULT_H);

  // Standard level annotations
  annotations.push(
    { y: 0,              label: "FFL ± 0.000",    type: "level" },
    { y: -FLOOR_SLAB_T,  label: `−${FLOOR_SLAB_T/1000}.000`,  type: "level" },
    { y: -PLINTH_H,      label: `−${PLINTH_H/1000}.000`,       type: "level" },
    { y: maxH,           label: `+${(maxH/1000).toFixed(3)}`,  type: "level" },
    { y: maxH + ROOF_SLAB_T, label: `+${((maxH+ROOF_SLAB_T)/1000).toFixed(3)}`, type: "level" },
  );

  return {
    sectionWalls,
    visibleWalls,
    annotations,
    cutLen: Math.round(cutLen),
    minX, maxX, maxH,
    floorY:    0,
    foundY:   -(PLINTH_H + FOUND_DEPTH),
    roofY:     maxH + ROOF_SLAB_T,
    floorSlabT: FLOOR_SLAB_T,
    roofSlabT:  ROOF_SLAB_T,
    plinthH:    PLINTH_H,
  };
}
